import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Save, PiggyBank, TrendingUp, Shield, DollarSign, Check, Loader2 } from 'lucide-react'
import { BudgetRecommendation, BudgetCategory } from '../../types/ai-budget'
import { useAuth } from '../../hooks/useAuth'
import SignInPrompt from '../SignInPrompt'

interface BudgetSaveCardProps {
  budgetRecommendation: BudgetRecommendation
  monthlyIncome: number
  onSaveBudget: (budgetName: string, description?: string) => Promise<void>
  className?: string
}

const BudgetSaveCard: React.FC<BudgetSaveCardProps> = ({
  budgetRecommendation,
  monthlyIncome,
  onSaveBudget,
  className = ''
}) => {
  const { user, isGuest } = useAuth()
  const [isExpanded, setIsExpanded] = useState(false)
  const [budgetName, setBudgetName] = useState(`AI Budget Plan - ${new Date().toLocaleDateString()}`)
  const [budgetDescription, setBudgetDescription] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const [showSignInPrompt, setShowSignInPrompt] = useState(false)

  const handleSaveBudget = async () => {
    // Check if user is in guest mode
    if (isGuest || !user) {
      setShowSignInPrompt(true)
      return
    }

    try {
      setIsSaving(true)
      await onSaveBudget(budgetName, budgetDescription || undefined)
      setIsSaved(true)
      
      // Reset state after showing success
      setTimeout(() => {
        setIsSaved(false)
        setIsExpanded(false)
      }, 2000)
    } catch (error) {
      console.error('Failed to save budget:', error)
      alert('Failed to save budget. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  // Calculate category totals
  const essentialTotal = budgetRecommendation.recommended_budget
    .filter(cat => cat.type === 'essential')
    .reduce((sum, cat) => sum + cat.allocated, 0)

  const savingsTotal = budgetRecommendation.recommended_budget
    .filter(cat => cat.type === 'savings')
    .reduce((sum, cat) => sum + cat.allocated, 0)

  const investmentTotal = budgetRecommendation.recommended_budget
    .filter(cat => cat.type === 'investment')
    .reduce((sum, cat) => sum + cat.allocated, 0)

  const discretionaryTotal = budgetRecommendation.recommended_budget
    .filter(cat => cat.type === 'discretionary')
    .reduce((sum, cat) => sum + cat.allocated, 0)

  const totalSavingsInvestment = savingsTotal + investmentTotal
  const savingsRate = Math.round((totalSavingsInvestment / monthlyIncome) * 100)

  const CategoryIcon = ({ type }: { type: BudgetCategory['type'] }) => {
    const iconClass = "w-4 h-4"
    switch (type) {
      case 'essential': return <Shield className={iconClass} />
      case 'savings': return <PiggyBank className={iconClass} />
      case 'investment': return <TrendingUp className={iconClass} />
      default: return <DollarSign className={iconClass} />
    }
  }

  const getTypeColor = (type: BudgetCategory['type']) => {
    switch (type) {
      case 'essential': return 'text-blue-600 bg-blue-50'
      case 'savings': return 'text-green-600 bg-green-50'
      case 'investment': return 'text-purple-600 bg-purple-50'
      default: return 'text-gray-600 bg-gray-50'
    }
  }

  if (isSaved) {
    return (
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className={`bg-green-500/10 border border-green-500/20 rounded-xl p-6 ${className}`}
      >
        <div className="flex items-center justify-center space-x-3 text-green-400">
          <Check className="w-6 h-6" />
          <span className="font-semibold text-lg">Budget Saved Successfully!</span>
        </div>
        <p className="text-center text-green-300 mt-2">
          Your personalized budget plan has been added to your profile.
        </p>
      </motion.div>
    )
  }

  return (
    <motion.div
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className={`bg-background-800 border border-accent-500/20 rounded-xl p-6 ${className}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-accent-500/20 rounded-lg">
            <PiggyBank className="w-6 h-6 text-accent-400" />
          </div>
          <div>
            <h3 className="font-bold text-lg text-background-100">🎯 Save This Budget Plan</h3>
            <p className="text-sm text-background-400">
              AI-generated personalized budget • {savingsRate}% savings rate
            </p>
          </div>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsExpanded(!isExpanded)}
          className="px-3 py-1 text-sm text-accent-400 hover:bg-accent-500/10 rounded-lg transition-colors"
        >
          {isExpanded ? 'Hide Details' : 'View Details'}
        </motion.button>
      </div>

      {/* Quick Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-background-750 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-blue-400">${essentialTotal.toLocaleString()}</div>
          <div className="text-xs text-background-500">Essentials</div>
        </div>
        <div className="bg-background-750 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-green-400">${savingsTotal.toLocaleString()}</div>
          <div className="text-xs text-background-500">Savings</div>
        </div>
        <div className="bg-background-750 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-purple-400">${investmentTotal.toLocaleString()}</div>
          <div className="text-xs text-background-500">Investments</div>
        </div>
        <div className="bg-background-750 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-background-200">${discretionaryTotal.toLocaleString()}</div>
          <div className="text-xs text-background-500">Discretionary</div>
        </div>
      </div>

      {/* Expanded Details */}
      <motion.div
        initial={false}
        animate={{ 
          height: isExpanded ? 'auto' : 0,
          opacity: isExpanded ? 1 : 0 
        }}
        className="overflow-hidden"
      >
        <div className="border-t border-blue-200 pt-4 mb-4">
          <h4 className="font-semibold text-gray-900 mb-3">Budget Categories</h4>
          <div className="space-y-2">
            {budgetRecommendation.recommended_budget.map((category, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-white rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`p-1.5 rounded ${getTypeColor(category.type)}`}>
                    <CategoryIcon type={category.type} />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{category.name}</div>
                    <div className="text-xs text-gray-500">{category.description}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-gray-900">${category.allocated.toLocaleString()}</div>
                  <div className="text-xs text-gray-500">{category.percentage}%</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Save Form */}
        <div className="border-t border-blue-200 pt-4">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Budget Name
              </label>
              <input
                type="text"
                value={budgetName}
                onChange={(e) => setBudgetName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="My Budget Plan"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (Optional)
              </label>
              <textarea
                value={budgetDescription}
                onChange={(e) => setBudgetDescription(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Add notes about your budget goals..."
              />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Save Button */}
      <div className="flex justify-end mt-4 pt-4 border-t border-blue-200">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleSaveBudget}
          disabled={isSaving || !budgetName.trim()}
          className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSaving ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Save className="w-5 h-5" />
          )}
          <span>{isSaving ? 'Saving...' : 'Save Budget to Profile'}</span>
        </motion.button>
      </div>

      {/* Key Benefits */}
      <div className="mt-4 p-3 bg-white rounded-lg border border-blue-100">
        <div className="text-sm text-gray-600">
          <span className="font-medium text-gray-900">💡 Benefits: </span>
          Track spending automatically • Get budget insights • Monitor progress toward goals
        </div>
      </div>

      {/* Sign In Prompt */}
      <SignInPrompt
        isOpen={showSignInPrompt}
        onClose={() => setShowSignInPrompt(false)}
        title="Sign in to save your budget"
        message="Create an account to save this personalized budget plan and track your progress over time."
        action="Sign In to Save"
      />
    </motion.div>
  )
}

export default BudgetSaveCard
